import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { Tabs, Descriptions, Tag, message } from "antd";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import Table from "components/Common/dataTable";
import CustomButton from "components/Common/ui/CustomButton";
// import AssignStudentsModal from "feature-module/class/components/AssignStudentsModal";
// import AddEditClassModal from "feature-module/class/components/AddEditClassModal";
import { getBranchLabelFromCode, getClassLevelLabelFromCode } from "utils/schoolUtils";
import { useAbility } from "config/abilityContext";

// --- Service (adapt path if different in your project)
import useClassManagement from "../hooks/useClassManagement";
import AddEditClassModal from "../components/AddEditClassModal";
import AssignStudentsModal from "../components/AssignStudentsModal";
import TabDetailClass from "../components/TabDetailClass";
import TabStudentClass from "../components/TabStudentClass";
import { ClassData } from "types/classTypes";
import Loader from "components/Common/loader";
import TabSubjectClass from "../components/TabSubjectClass";

// --- Types (adapt to your project)
interface ClassLevel {
    id: string;
    name: string;
    labelKey: string;
    schoolCycle: "preschool" | "primary_school" | "secondary_school" | "high_school";
}

interface Branch { id: string; labelKey: string; }

interface ClassStudentRow {
    id: string;
    student: {
        id: string;
        fullName: string;
        code?: string;
        gender?: string;
        status?: string;
    };
}

interface ClassEntity {
    id: string;
    name: string;
    noOfStudents?: number;
    classLevel?: ClassLevel;
    branch?: Branch | null;
    students?: ClassStudentRow[]; // if API embeds it; otherwise fetched separately
}

const DetailClassPage: React.FC = () => {
  const { t } = useTranslation();
  const ability = useAbility();
  const { classId } = useParams<{ classId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { handleGetClassById, detailClass, openAssignModal, handleAssignStudentsSave, isAssignModalOpen, allStudents, assignedStudents, setAssignModalOpen, handleGetAssignedStudents } = useClassManagement();

  const [studentsRefreshTrigger, setStudentsRefreshTrigger] = useState(0);

  const reloadDetailClass = () => {
    handleGetClassById(classId as string);
  };

  useEffect(() => {
    if (!classId) return;
    async function fetchData() {
      setLoading(true);
      await handleGetClassById(classId as string);
      setLoading(false);
    }
    fetchData();
  }, [classId]);

  const handleAssignStudentsSaveWithRefresh = async (classId: string, students: any[]) => {
    await handleAssignStudentsSave(classId, students);
    setStudentsRefreshTrigger(prev => prev + 1); 
  };

  const breadcrumbItems = useMemo(
    () => [
      { label: t("dashboard"), path: all_routes.dashboard },
      { label: t("classes_list"), path: all_routes.classes },
      { label: detailClass?.name || t("class_detail"), active: true },
    ],
    [detailClass?.name, t]
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={detailClass?.name || t("class_detail")} breadcrumbItems={breadcrumbItems} />

        <Tabs defaultActiveKey={searchParams.get("tab") || "details"} onChange={key => setSearchParams({ tab: key })}>
          <Tabs.TabPane tab={t("details")} key="details">
            <TabDetailClass
              detailClass={detailClass || null}
            />
          </Tabs.TabPane>
          {
            ability.can("getAll", "Student") && (
              <Tabs.TabPane tab={t("students")} key="students">
                <TabStudentClass
                  loading={loading}
                  canUpdate={ability.can("update", "Class")}
                  onOpenAssign={() => openAssignModal(classId || "")}
                  classId={classId || ""}
                  refreshTrigger={studentsRefreshTrigger} // Pass the trigger
                />
              </Tabs.TabPane>
            )
          }
          {
            (ability.can("getAll", "Teacher") || ability.can("update", "Class")) && (
              <Tabs.TabPane tab={t("teachers")} key="teachers">
                <TabSubjectClass subjects={detailClass?.classSubject || []} reloadDetailClass={reloadDetailClass} />
              </Tabs.TabPane>
            )
          }
        </Tabs>

        <AssignStudentsModal
          classId={classId || ""}
          isOpen={isAssignModalOpen}
          allStudents={allStudents}
          assignedStudents={assignedStudents}
          onClose={() => setAssignModalOpen(false)}
          onSave={handleAssignStudentsSaveWithRefresh} // Use the modified handler
        />

        <AddEditClassModal
          classData={detailClass as any}
          isOpen={isEditOpen}
          onSave={async () => {
            setIsEditOpen(false);
            await handleGetClassById(classId ?? "");
          }}
          onClose={() => setIsEditOpen(false)}
          subjects={[]}
          classLevels={detailClass?.classLevel ? [detailClass.classLevel] : []}
        />
      </div>
    </div>
  );
};

export default DetailClassPage;
